import { actions as usersActions } from '@/api/users';
import BasicField from '@payaca/components/basicField/BasicField';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import PhoneNumberField from '@payaca/components/phoneNumberField/PhoneNumberField';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import { TIconName } from '@payaca/untitled-icons';
import { FunctionComponent, useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import './LoqateAdvisoryPhoneInputField.sass';

type Props = {
  styleVariant?: InputStyleVariant;
  name: string;
  value?: any;
  label?: string;
  description?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  iconBefore?: TIconName;
  onChange?: (value: { [key: string]: string }) => void;
  onTouch?: (fieldName: string) => void;
  disableValidation?: boolean;
  inputType?: 'phone' | 'basic';
  onValidating?: () => void;
  onValidated?: (isValid: boolean) => void;
};

const LoqateAdvisoryPhoneInputField: FunctionComponent<Props> = ({
  styleVariant,
  name,
  value,
  label,
  description,
  isDisabled = false,
  isRequired = false,
  iconBefore,
  onChange,
  onTouch,
  onValidating,
  onValidated,
  disableValidation = false,
  inputType = 'phone',
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const [_loqateValidationTimeout, setLoqateValidationTimeout] =
    useState<ReturnType<typeof setTimeout>>();
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState<boolean>();

  useEffect(() => {
    return () => {
      setLoqateValidationTimeout((loqateValidationTimeout) => {
        if (loqateValidationTimeout) {
          clearTimeout(loqateValidationTimeout);
        }
        return undefined;
      });
    };
  }, []);

  const runLoqateValidation = useCallback(
    (value: any) => {
      if (!value || value === '') {
        setIsValid(undefined);
        return;
      }

      setIsValidating(true);
      onValidating?.();
      dispatch(
        usersActions.validatePhone(value, (error: Error, response: any) => {
          setIsValidating(false);
          if (error) {
            setIsValid(undefined);
            onValidated?.(false);
          } else {
            setIsValid(response);
            onValidated?.(response);
          }
        })
      );
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'onValidating', and 'onValidated'
    [dispatch]
  );

  const iconAfter: TIconName | undefined = useMemo(() => {
    if (isValidating) return 'loading-01.3';
    if (isValid === true) return 'check';
    if (isValid === false) return 'x.3';
  }, [isValidating, isValid]);

  const validationResult = useMemo(() => {
    if (isValidating || isValid === undefined) return;
    return {
      isValid: isValid,
      errors: isValid
        ? undefined
        : [
            'This phone number looks invalid to us, please double check before continuing',
          ],
    };
  }, [isValidating, isValid]);

  useEffect(() => {
    if (!disableValidation) {
      setLoqateValidationTimeout((loqateValidationTimeout) => {
        if (loqateValidationTimeout) {
          clearTimeout(loqateValidationTimeout);
        }
        return setTimeout(() => {
          runLoqateValidation(value);
        }, 500);
      });
    }
  }, [value, runLoqateValidation, disableValidation]);

  useEffect(() => {
    if (disableValidation) {
      setIsValidating(false);
      setIsValid(undefined);
      setLoqateValidationTimeout((loqateValidationTimeout) => {
        if (loqateValidationTimeout) {
          clearTimeout(loqateValidationTimeout);
        }
        return undefined;
      });
    }
  }, [disableValidation]);

  return (
    <div className="loqate-advisory-phone-input-field">
      <ValidatedFieldWrapper
        isTouched={value && value !== ''}
        validationResult={validationResult}
      >
        <>
          {inputType === 'phone' && (
            <PhoneNumberField
              styleVariant={styleVariant}
              name={name}
              value={value}
              label={label}
              description={description}
              isDisabled={isDisabled}
              isRequired={isRequired}
              iconBefore={iconBefore}
              onChange={(data) => {
                // ensure the area code cannot be removed (i.e +44, +61 etc)
                if (data[name].length > 3) {
                  onChange?.(data);
                }
              }}
              onTouch={onTouch}
              iconAfter={iconAfter}
            />
          )}
          {inputType === 'basic' && (
            <BasicField
              styleVariant={styleVariant}
              name={name}
              value={value}
              label={label}
              description={description}
              isDisabled={isDisabled}
              isRequired={isRequired}
              iconBefore={iconBefore || 'phone.3'}
              onChange={onChange}
              onTouch={onTouch}
              iconAfter={iconAfter}
              type="tel"
            />
          )}
        </>
      </ValidatedFieldWrapper>
    </div>
  );
};

export default LoqateAdvisoryPhoneInputField;
